/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useMemo, useState } from 'react';
import './Companies.css';
import SearchableSelect from '../../components/shared/SearchableSelect';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import StatusBadge from '../../components/shared/StatusBadge';
import StatusFilterTabs from '../../components/shared/StatusFilterTabs';
import {
  ENTITY_STATUS_SELECT_OPTIONS,
  ENTITY_STATUS_TABS,
  matchesEntityStatus,
} from '../../utils/status';
import { exportRowsToExcel } from '../../utils/excelExport';
import { withExcelExportFeedback } from '../../utils/backendExport';
import {
  GridColumnFilterButton,
  GridColumnFilterPanel,
} from '../../components/shared/GridColumnFilterPanel';
import { GridExportButton, GridSearchInput } from '../../components/shared/GridToolbar';
import { useGridColumnFilters } from '../../hooks/useGridColumnFilters';
import { matchesScalarField } from '../../utils/gridColumnFilters';
import { accentAvatarStyle, ActiveGridFiltersBar } from '../../components/shared/agGridCells';
import {
  BsBuildings,
  BsCheckCircle,
  BsCurrencyEuro,
  BsPeopleFill,
  BsShop,
  BsGlobe2,
  BsPlusCircleFill,
} from 'react-icons/bs';
import type { Company, CompanyFormState } from './models/types';
import { createBlankCompanyForm, getMaxCompanyRevenue, normalizeCompanyForm } from './utils/utils';
import { ACCENT_COLORS, COMPANY_EXPORT_COLUMNS, EMPTY_COMPANY_COLUMN_FILTERS, INITIAL_COMPANIES } from './constants/constants';
import { COMPANY_COLUMN_FILTER_LABELS } from './constants/constants';
import { buildCompanyColumnFilterFields } from './table/columnDefs';

const ADD_FORM_FIELDS = [
  { label: 'Company Name', name: 'name' },
  { label: 'Legal Name', name: 'legalName' },
  { label: 'Country', name: 'country' },
  { label: 'Industry', name: 'industry' },
  { label: 'Legal Type', name: 'type' },
  { label: 'Tax ID', name: 'taxId' },
  { label: 'Contact Email', name: 'contact' },
  { label: 'Employees', name: 'employees' },
  { label: 'Annual Revenue (€)', name: 'revenue' },
  { label: 'Linked Acquirer', name: 'acquirer' },
  { label: 'Incorporated Year', name: 'incorporatedYear' },
] as const;

const EDIT_FORM_FIELDS = ADD_FORM_FIELDS.filter((field) => field.name !== 'incorporatedYear');

const Companies: React.FC = () => {
  const [companies, setCompanies] = useState(INITIAL_COMPANIES);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<Partial<CompanyFormState>>({});
  const [addForm, setAddForm] = useState<CompanyFormState>(createBlankCompanyForm());
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [exporting, setExporting] = useState(false);
  const {
    columnFilters,
    filterPanelOpen,
    setFilterPanelOpen,
    activeColumnFilters,
    activeColumnFilterCount,
    handleRemoveColumnFilter,
    handleClearColumnFilters,
    handleApplyColumnFilters,
  } = useGridColumnFilters(EMPTY_COMPANY_COLUMN_FILTERS, COMPANY_COLUMN_FILTER_LABELS);

  const maxRevenue = useMemo(() => getMaxCompanyRevenue(companies), [companies]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await withExcelExportFeedback(async () => {
        await exportRowsToExcel(
          filtered.map((c) => ({
            name: c.name,
            country: c.country,
            industry: c.industry,
            type: c.type,
            status: c.status,
            revenue: c.revenue,
            employees: c.employees,
            acquirer: c.acquirer,
            merchants: c.merchants,
          })),
          COMPANY_EXPORT_COLUMNS,
          { sheetName: 'Companies', fileNamePrefix: 'companies' },
        );
      }, { error: 'Failed to export companies.' });
    } finally {
      setExporting(false);
    }
  };

  const handleEdit = (idx: number) => {
    setEditIdx(idx);
    setForm({ ...companies[idx] });
    setShowModal(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editIdx !== null) {
      setCompanies((prev) =>
        prev.map((c, i) => (i === editIdx ? { ...c, ...form } as Company : c)),
      );
      setShowModal(false);
      setEditIdx(null);
    }
  };

  const handleAddChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newCo = normalizeCompanyForm(addForm);
    setCompanies((prev) => [newCo, ...prev]);
    setAddForm(createBlankCompanyForm());
    setShowAdd(false);
  };

  const filtered = companies.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch =
      c.name.toLowerCase().includes(q) ||
      c.country.toLowerCase().includes(q) ||
      c.taxId.toLowerCase().includes(q) ||
      c.contact.toLowerCase().includes(q);
    const matchStatus = matchesEntityStatus(c.status, filterStatus);
    if (!matchSearch || !matchStatus) return false;
    if (!matchesScalarField(c.country, columnFilters.country)) return false;
    if (!matchesScalarField(c.industry, columnFilters.industry)) return false;
    if (!matchesScalarField(c.status, columnFilters.status)) return false;
    return true;
  });

  const columnFilterFields = useMemo(
    () => buildCompanyColumnFilterFields(companies),
    [companies],
  );

  const activeCount = companies.filter((c) => c.status === 'Active').length;
  const totalRev = companies.reduce((s, c) => s + c.revenue, 0);
  const totalEmp = companies.reduce((s, c) => s + c.employees, 0);
  const totalMerch = companies.reduce((s, c) => s + c.merchants, 0);
  const countries = new Set(companies.map((c) => c.country)).size;

  const kpis = [
    { label: 'Total Companies', value: companies.length.toString(), change: '+1 this month', color: '#5c6bc0', icon: <BsBuildings /> },
    { label: 'Active', value: activeCount.toString(), change: `${activeCount}/${companies.length}`, color: '#2ed573', icon: <BsCheckCircle /> },
    { label: 'Total Revenue', value: `€${(totalRev / 1000000).toFixed(2)}M`, change: '+11.8%', color: '#26a69a', icon: <BsCurrencyEuro /> },
    { label: 'Total Employees', value: totalEmp.toLocaleString(), change: 'Across all cos.', color: '#a78bfa', icon: <BsPeopleFill /> },
    { label: 'Merchants Linked', value: totalMerch.toString(), change: 'Assigned', color: '#ffa502', icon: <BsShop /> },
    { label: 'Countries', value: countries.toString(), change: 'European reach', color: '#ff6b6b', icon: <BsGlobe2 /> },
  ];

  return (
    <div className="dh-page">
      <div className="dh-page-header">
        <div>
          <h1 className="dh-page-title">Companies</h1>
          <p className="dh-page-sub">
            Monday, 18 February 2026 &middot; {companies.length} registered companies across {countries} countries
          </p>
        </div>
        <div className="dh-header-actions">
          <button className="um-invite-btn" type="button" onClick={() => setShowAdd(true)}>
            <BsPlusCircleFill /> Add Company
          </button>
        </div>
      </div>

      <div className="dh-kpi-grid">
        {kpis.map((k, i) => (
          <div className="dh-kpi-card" key={k.label} style={{ '--kpi-color': k.color, animationDelay: `${i * 0.05}s` } as React.CSSProperties}>
            <div className="dh-kpi-top">
              <span className="dh-kpi-icon">{k.icon}</span>
              <span className="dh-kpi-badge up">{k.change}</span>
            </div>
            <div className="dh-kpi-value">{k.value}</div>
            <div className="dh-kpi-label">{k.label}</div>
            <div className="acq-accent-line" style={{ background: k.color }} />
          </div>
        ))}
      </div>

      <div className="dh-table-controls">
        <div className="dh-table-controls-left">
          <StatusFilterTabs
            tabs={ENTITY_STATUS_TABS}
            value={filterStatus}
            onChange={setFilterStatus}
            getCount={(tab) => {
              const key = typeof tab === 'string' ? tab : tab.value;
              return key === 'All'
                ? companies.length
                : companies.filter((c) => matchesEntityStatus(c.status, key)).length;
            }}
          />
        </div>
        <div className="dh-table-controls-right">
          <GridSearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search name, country, tax ID..."
          />
          <GridColumnFilterButton
            activeCount={activeColumnFilterCount}
            onClick={() => setFilterPanelOpen(true)}
          />
          <GridExportButton
            onClick={handleExport}
            loading={exporting}
            disabled={filtered.length === 0}
          />
        </div>
      </div>

      <div className="dh-table-section">
        <ActiveGridFiltersBar
          filters={activeColumnFilters}
          onRemove={handleRemoveColumnFilter}
          onClearAll={handleClearColumnFilters}
        />
        <div
          className={`dh-table-wrapper ${filtered.length === 0 ? 'dh-table-wrapper-no-scroll' : ''}`}
        >
          <table className="dh-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Country</th>
                <th>Industry</th>
                <th>Type</th>
                <th>Status</th>
                <th>Annual Revenue</th>
                <th>Employees</th>
                <th>Linked Acquirer</th>
                <th>Merchants</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const realIdx = companies.indexOf(c);
                const initials = c.name
                  .split(' ')
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase();
                const accent = ACCENT_COLORS[realIdx % ACCENT_COLORS.length];
                const revPct = Math.round((c.revenue / maxRevenue) * 100);
                return (
                  <tr key={c.id} className="dh-tr">
                    <td>
                      <div className="acq-name-cell">
                        <div className="acq-avatar" style={accentAvatarStyle(accent)}>
                          {initials}
                        </div>
                        <div>
                          <div className="acq-name">{c.name}</div>
                          <div className="acq-key co-tax-id">Tax: {c.taxId}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className="dh-merchant">{c.country}</span></td>
                    <td><span className="mm-category-chip">{c.industry}</span></td>
                    <td><span className="co-type-badge">{c.type}</span></td>
                    <td><StatusBadge status={c.status} /></td>
                    <td>
                      <div className="acq-vol-wrap">
                        <span className="dh-amount">
                          {c.revenue > 0 ? `€${c.revenue.toLocaleString()}` : '—'}
                        </span>
                        <div className="acq-vol-bar-track">
                          <div
                            className="acq-vol-bar-fill"
                            style={{ width: `${revPct}%`, background: accent }}
                          />
                        </div>
                      </div>
                    </td>
                    <td><span className="dh-amount">{c.employees}</span></td>
                    <td><span className="co-acquirer">{c.acquirer}</span></td>
                    <td>
                      <span className={`co-merchants-count ${c.merchants === 0 ? 'zero' : ''}`}>
                        {c.merchants}
                      </span>
                    </td>
                    <td>
                      <button className="acq-edit-btn" type="button" onClick={() => handleEdit(realIdx)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="mm-empty">
                    No companies match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <div className="fin-modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="fin-modal acq-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fin-modal-header">
              <h2 className="fin-modal-title">Add Company</h2>
              <button className="fin-modal-close" type="button" onClick={() => setShowAdd(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleAddSubmit} className="fin-form">
              <div className="fin-form-grid">
                {ADD_FORM_FIELDS.map((f) => (
                  <label key={f.name} className="fin-label">
                    <span>{f.label}</span>
                    <Input
                      name={f.name}
                      value={(addForm as any)[f.name] ?? ''}
                      onChange={handleAddChange}
                      required={['name', 'country', 'taxId', 'contact'].includes(f.name)}
                    />
                  </label>
                ))}
                <label className="fin-label">
                  <span>Status</span>
                  <SearchableSelect
                    className="h-9"
                    value={addForm.status}
                    options={ENTITY_STATUS_SELECT_OPTIONS}
                    onChange={(value) => setAddForm((prev) => ({ ...prev, status: value }))}
                    placeholder="Select status"
                    searchPlaceholder="Search status…"
                    aria-label="Status"
                  />
                </label>
              </div>
              <div className="fin-modal-actions">
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Add Company
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fin-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="fin-modal acq-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fin-modal-header">
              <h2 className="fin-modal-title">Edit Company</h2>
              <button className="fin-modal-close" type="button" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit} className="fin-form">
              <div className="fin-form-grid">
                {EDIT_FORM_FIELDS.map((f) => (
                  <label key={f.name} className="fin-label">
                    <span>{f.label}</span>
                    <Input
                      name={f.name}
                      value={(form as any)[f.name] ?? ''}
                      onChange={handleChange}
                      required
                    />
                  </label>
                ))}
                <label className="fin-label">
                  <span>Status</span>
                  <SearchableSelect
                    className="h-9"
                    value={form.status ?? 'Active'}
                    options={ENTITY_STATUS_SELECT_OPTIONS}
                    onChange={(value) => setForm((prev) => ({ ...prev, status: value }))}
                    placeholder="Select status"
                    searchPlaceholder="Search status…"
                    aria-label="Status"
                  />
                </label>
                <label className="fin-label">
                  <span>Incorporated Year</span>
                  <Input
                    name="incorporatedYear"
                    value={(form as any).incorporatedYear ?? ''}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div className="fin-modal-actions">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <GridColumnFilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        fields={columnFilterFields}
        values={columnFilters}
        onApply={handleApplyColumnFilters}
        onClear={handleClearColumnFilters}
      />
    </div>
  );
};

export default Companies;
